import { currentRoute, isBrowserPage } from '../ui/route.ts';

// A browser tab opened at /dash/<name> shows that dashboard of the server, and only in this tab
// (docs/specs/settings.md §4.7, the owner 2026-11-04): /dash goes on showing the browser's own
// choice, so two tabs can show two dashboards. The tab keeps its dashboard's cached copy and source
// under a prefix of its own in the browser's storage, so that loading, saving and reloading work
// as for the browser's own. The tab remembers its dashboard (sessionStorage) while it shows other
// pages, the settings for one.

const TAB_KEY = 'casa.tabDashboard';

/** A name as it is written in a path: "Main Tablet" is main-tablet. */
export function dashboardSlug(name: string) : string {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/** The dashboard a path names, from the server's: the exact name first, else the one whose slug it is. */
export function dashboardBySlug(slug: string, names: readonly string[]) : string | undefined {
    return names.find((name) => name === slug) ?? names.find((name) => dashboardSlug(name) === dashboardSlug(slug));
}

function remembered() : string | undefined {
    try {
        return sessionStorage.getItem(TAB_KEY) ?? undefined;
    } catch {
        return undefined;
    }
}

function remember(slug: string | undefined) : void {
    try {
        if (slug === undefined) {
            sessionStorage.removeItem(TAB_KEY);
        } else {
            sessionStorage.setItem(TAB_KEY, slug);
        }
    } catch {
        // Without session storage the tab forgets its dashboard on other pages; it shows the browser's then.
    }
}

/** The slug of the dashboard this tab shows by its path, as the page started; undefined for the browser's own. */
function tabFromRoute() : string | undefined {
    if (!isBrowserPage() || typeof location === 'undefined') {
        return undefined;
    }

    const route = currentRoute();

    if (route.startsWith('/dash/')) {
        let slug: string;

        try {
            slug = dashboardSlug(decodeURIComponent(route.slice('/dash/'.length)));
        } catch {
            slug = '';
        }

        remember(slug === '' ? undefined : slug);

        return slug === '' ? undefined : slug;
    }

    if (route === '/dash' || route === '/') {
        remember(undefined);

        return undefined;
    }

    return remembered();
}

const TAB = tabFromRoute();

/** The slug of the dashboard this tab shows by its path; undefined when it shows the browser's own. */
export function tabDashboard() : string | undefined {
    return TAB;
}

/** Where this tab keeps its dashboard's copy and source in the browser's storage: its own prefix, or none. */
export function tabStoragePrefix() : string {
    return TAB === undefined ? '' : `tabs/${TAB}/`;
}

/** The route this tab's dashboard is at, to come back to from other pages. */
export function tabDashboardRoute() : string {
    return TAB === undefined ? '/dash' : `/dash/${TAB}`;
}
